import { Autocomplete, Box, FilterOptionsState, FormControl, TextField, Typography } from '@mui/material'
import { Controller, FieldValues } from 'react-hook-form'
import { InputProps } from '../../../store/types'
import React, { HTMLAttributes, ReactNode } from 'react'
import { isEqual } from 'lodash'
import { useDebouncedScroll } from '../../../store/utils/useDebouncedScroll'

export type Option = {
  label: string
  value: string
}

interface AutocompleteInputProps<T extends FieldValues> extends InputProps<T> {
  options: Option[]
  onSelectBottomScroll?: () => void
  noOptionsText?: string
  groupBy?: (option: Option) => string
  listboxMaxHeight?: number | string
  handleInputChange?: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void
  multiple?: boolean
  disableCloseOnSelect?: boolean
  renderOption?: (props: HTMLAttributes<HTMLLIElement>, option: Option, state: { selected: boolean }) => ReactNode
  size?: 'small' | 'medium'
  limitTags?: number
  sortValues?: (values: Option[]) => Option[]
  required?: boolean
  filterOptions?: (options: Option[], state: FilterOptionsState<Option>) => Option[]
}

const AutocompleteInput = <T extends FieldValues>({
  name,
  control,
  label,
  options,
  rules,
  onSelectBottomScroll,
  noOptionsText,
  groupBy,
  listboxMaxHeight,
  handleInputChange,
  multiple,
  disableCloseOnSelect,
  renderOption,
  size,
  limitTags,
  sortValues,
  required,
  filterOptions,
  disabled,
}: AutocompleteInputProps<T>) => {
  const onScroll = useDebouncedScroll(onSelectBottomScroll)

  return (
    <FormControl fullWidth size={'small'} sx={{ maxHeight: size === 'small' ? 36 : 'auto' }}>
      <Controller
        control={control}
        name={name}
        rules={rules}
        render={({ field: { onChange, value }, fieldState: { error } }) => {
          return (
            <Autocomplete
              disabled={disabled}
              size={size}
              value={value || null}
              limitTags={limitTags}
              onChange={(_, newValue) => {
                if (multiple) {
                  let updatedValues = (newValue as Option[]) || []
                  if (sortValues) {
                    updatedValues = sortValues(updatedValues)
                  }
                  onChange(updatedValues)
                } else {
                  onChange((newValue as Option) || null)
                }
              }}
              options={options}
              getOptionLabel={option => option.label || ''}
              noOptionsText={noOptionsText}
              groupBy={groupBy}
              multiple={value ? multiple : false}
              disableCloseOnSelect={disableCloseOnSelect}
              renderGroup={params => (
                <div key={params.key}>
                  <Box sx={{ px: 1.5, py: 1 }}>
                    <Typography variant="body1" sx={{ color: 'text.highEmphasis' }}>
                      {params.group}
                    </Typography>
                  </Box>
                  <Box sx={{ color: 'text.base' }}>{params.children}</Box>
                </div>
              )}
              renderInput={params => (
                <TextField
                  {...(params as any)}
                  onChange={handleInputChange}
                  size={'small'}
                  required={required}
                  label={label}
                  error={!!error}
                  helperText={error ? error.message : undefined}
                />
              )}
              ListboxProps={{
                style: { maxHeight: listboxMaxHeight || 200 },
                onScroll: onScroll,
              }}
              renderOption={renderOption}
              isOptionEqualToValue={(option, value) => {
                return isEqual(option.value, value.value)
              }}
              filterOptions={filterOptions}
            />
          )
        }}
      />
    </FormControl>
  )
}

export default AutocompleteInput